'use client'

import Link from 'next/link'
import { formatEventDateRange } from '@/lib/timed-ui/formatting'
import { useFormatPrice } from '@/lib/timed-ui/use-format-price'
import Badge from '../common/Badge'
import type { TimedUIEvent } from '@/lib/timed-ui/adapters'

interface EventListItemProps {
  event: TimedUIEvent
  hideLocation?: boolean
}

const EventListItem = ({ event, hideLocation }: EventListItemProps) => {
  const formatPrice = useFormatPrice()
  const eventTitle = event.title || event.name || 'Untitled Event'
  const isSoldOut = event.status === 'SOLD_OUT'
  const href = `/events/${event.slug || event.id}`
  // Filter first, then check (see EventContentSection): all-free or all-add-on
  // ticket lists otherwise reduced to Math.min() of nothing, "From $∞".
  const pricedTickets = event.ticketTypes.filter(t => t.allInPrice > 0 && !t.isAddon)
  const minPrice = pricedTickets.length > 0 ? Math.min(...pricedTickets.map(t => t.allInPrice)) : 0

  return (
    <Link
      href={href}
      className={`flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 sm:py-3.5 bg-surface border border-surface-border rounded-xl cursor-pointer shadow-card hover:border-primary/30 hover:shadow-card-hover active:bg-surface-secondary transition-all duration-150 animate-fade-in touch-manipulation ${isSoldOut ? 'opacity-50' : ''}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold text-text-primary text-sm truncate">{eventTitle}</h3>
          {isSoldOut && <Badge variant="error">Sold Out</Badge>}
        </div>
        {event.venue && (
          <div className="flex items-center gap-1.5 mt-0.5">
            <svg className="w-3 h-3 text-text-tertiary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            </svg>
            <span className="text-xs text-text-tertiary truncate">{event.venue.name}</span>
          </div>
        )}
        {event.venueCity && (
          <span className="sm:hidden text-xs text-text-tertiary mt-0.5 block truncate">{event.venueCity}</span>
        )}
      </div>

      {!hideLocation && (
        <div className="hidden sm:block text-xs text-text-tertiary truncate w-36 text-center">
          {event.venueCity || ''}
        </div>
      )}

      <div className="flex-shrink-0 text-right">
        {event.startDate && (
          <div className="text-xs sm:text-sm font-medium text-text-primary whitespace-nowrap">
            {formatEventDateRange(event.startDate, event.endDate, false, event.timezone)}
          </div>
        )}
        {minPrice > 0 && (
          <div className="text-xs text-primary font-semibold">
            From {formatPrice(minPrice)}
          </div>
        )}
      </div>

      <svg className="w-4 h-4 text-text-tertiary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </Link>
  )
}

export default EventListItem
